import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 600000, // 10 minutes
});

// Podcast Generation API
export const podcastApi = {
  // Generate podcast
  generate: async (gameData, customInstructions = '', teamName, playerRoster) => {
    const response = await api.post('/api/podcasts/generate', {
      gameData,
      customInstructions,
      teamName,
      playerRoster,
    });
    return response.data;
  },

  // Check podcast status
  checkStatus: async (jobId) => {
    if (!jobId || jobId === 'undefined' || jobId === 'null') {
      throw new Error('Invalid job ID provided to checkStatus');
    }
    const response = await api.get(`/api/podcasts/status/${jobId}`);
    return response.data;
  },

  // Poll for completion
  pollForCompletion: async (jobId, onProgress = null) => {
    const maxAttempts = 540; // 45 minutes max (5s intervals)
    let attempts = 0;

    // Map AutoContent status codes to entertaining stage labels
    const statusLabel = (code) => {
      if (code >= 100) return '🎉 Wrapping up the broadcast booth...';
      if (code >= 80) return '🎙️ Recording the final takes...';
      if (code >= 60) return '🔊 Laying down the audio track...';
      if (code >= 30) return '📝 Writing witty commentary...';
      if (code >= 10) return '⚾ Studying the box score like a pro scout...';
      return '🎭 Setting up the broadcast booth...';
    };

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          attempts++;
          const result = await podcastApi.checkStatus(jobId);

          // Use real AutoContent status code (0-100) as progress if available,
          // otherwise fall back to time-based estimate capped at 95%
          const rawCode = result.autocontentStatusCode ?? null;
          const progress = rawCode !== null
            ? Math.min(rawCode, 99) // cap at 99 until truly complete
            : Math.min((attempts / maxAttempts) * 95, 95);

          if (onProgress) {
            onProgress({
              attempt: attempts,
              maxAttempts,
              status: result.status,
              progress,
              statusLabel: statusLabel(rawCode ?? 0)
            });
          }

          if (result.status === 'completed') {
            resolve(result);
          } else if (result.status === 'failed') {
            reject(new Error('Podcast generation failed. Please try again.'));
          } else if (attempts >= maxAttempts) {
            reject(new Error('Podcast generation timed out after 45 minutes.'));
          } else {
            setTimeout(poll, 5000);
          }
        } catch (error) {
          if (attempts >= maxAttempts) {
            reject(error);
          } else {
            setTimeout(poll, 5000);
          }
        }
      };

      poll();
    });
  },

  // Get all podcasts for the logged-in user
  getMyPodcasts: () => api.get(`/api/podcasts/my-podcasts?t=${Date.now()}`),

  // Get loading messages
  getLoadingMessages: () => api.get('/api/podcasts/loading-messages'),

  // Delete a podcast
  deletePodcast: (podcastId) => api.delete(`/api/podcasts/delete/${podcastId}`),

  // Publish a podcast (make public)
  publishPodcast: async (id) => {
    const res = await api.post(`/api/podcasts/${id}/publish`);
    return res.data;
  },

  // Unpublish a podcast (make private)
  unpublishPodcast: async (id) => {
    const res = await api.post(`/api/podcasts/${id}/unpublish`);
    return res.data;
  },

  // Get public podcast metadata by shareId
  getPublicPodcast: async (shareId) => {
    const res = await api.get(`/api/podcasts/public/${shareId}`);
    return res.data;
  },

  // Unlock the full episode of a preview-only podcast (spends 1 credit)
  unlockPodcast: async (id) => {
    const res = await api.post(`/api/podcasts/${id}/unlock`);
    return res.data;
  },

  // Upload a single box score file and return its AutoContent file ID
  uploadBoxScore: async (file) => {
    const uploadFormData = new FormData();
    uploadFormData.append('boxScore', file);
    const uploadResponse = await api.post('/api/podcasts/upload-pdf', uploadFormData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const fileId = uploadResponse.data.fileId;
    if (!fileId) {
      throw new Error('Box score upload did not return a file ID.');
    }
    return fileId;
  },

  // Create podcast. Accepts either:
  //  - a structured object: { teamId, gameType, eventName, highlights, games: [{ opponentName, usersTeamScore, opponentScore, files: [File], boxScoreText, gameLabel }] }
  //  - (legacy) a FormData instance for single-game generation
  createPodcast: async (input, onProgress = null) => {
    try {
      // Normalize legacy FormData input into the structured object shape.
      let options;
      if (typeof FormData !== 'undefined' && input instanceof FormData) {
        const files = [];
        const f1 = input.get('boxScore');
        if (f1) files.push(f1);
        options = {
          teamId: input.get('teamId') || '',
          gameType: 'single',
          eventName: '',
          highlights: input.get('highlights') || '',
          games: [{
            opponentName: input.get('opponentName') || '',
            usersTeamScore: input.get('usersTeamScore') || '',
            opponentScore: input.get('opponentScore') || '',
            files,
            boxScoreText: input.get('boxScoreText') || '',
            gameLabel: '',
          }],
        };
      } else {
        options = input || {};
      }

      const {
        teamId = '',
        gameType = 'single',
        eventName = '',
        highlights = '',
        games = [],
      } = options;

      // Step 1: Resolve team name
      let teamName = '';
      if (teamId) {
        const teamResponse = await api.get(`/api/teams/${teamId}`);
        teamName = teamResponse.data.teamName;
      }

      const normalizedType = (gameType === 'doubleheader' || gameType === 'tournament') ? gameType : 'single';
      const isMultiGame = normalizedType !== 'single';

      let generatePayload = { teamName, teamId };
      if (highlights && highlights.trim()) {
        generatePayload.highlights = highlights.trim();
      }

      if (isMultiGame) {
        // Build a games array, uploading all box score files per game.
        const builtGames = [];
        for (const g of games) {
          const pdfFileIds = [];
          for (const file of (g.files || [])) {
            pdfFileIds.push(await podcastApi.uploadBoxScore(file));
          }
          const hasFiles = pdfFileIds.length > 0;
          const text = (g.boxScoreText || '').trim();
          if (!hasFiles && !text) continue; // skip empty games
          builtGames.push({
            opponentName: g.opponentName || '',
            usersTeamScore: g.usersTeamScore !== '' && g.usersTeamScore != null ? String(g.usersTeamScore) : '',
            opponentScore: g.opponentScore !== '' && g.opponentScore != null ? String(g.opponentScore) : '',
            gameLabel: g.gameLabel || '',
            pdfFileIds,
            textContent: text,
          });
        }
        if (builtGames.length === 0) {
          throw new Error('Please add at least one game with a box score or description.');
        }
        generatePayload.gameType = normalizedType;
        generatePayload.eventName = eventName || '';
        generatePayload.games = builtGames;
      } else {
        // Single-game path (legacy backend fields).
        const g = games[0] || {};
        const opponentName = g.opponentName || '';
        const usersTeamScore = g.usersTeamScore !== '' && g.usersTeamScore != null ? String(g.usersTeamScore) : '';
        const opponentScore = g.opponentScore !== '' && g.opponentScore != null ? String(g.opponentScore) : '';
        const boxScoreText = (g.boxScoreText || '').trim();
        const files = g.files || [];
        generatePayload.opponentName = opponentName;

        if (files.length > 0) {
          generatePayload.pdfFileId = await podcastApi.uploadBoxScore(files[0]);
          if (usersTeamScore !== '' && opponentScore !== '') {
            generatePayload.scores = `${teamName} ${usersTeamScore} - ${opponentName || 'Opponent'} ${opponentScore}`;
          }
        } else if (boxScoreText) {
          let textContent = boxScoreText;
          if (usersTeamScore !== '' && opponentScore !== '') {
            textContent = `Final Score: ${teamName} ${usersTeamScore} - ${opponentName || 'Opponent'} ${opponentScore}\n\n${textContent}`;
          }
          if (highlights && highlights.trim()) {
            textContent += `\n\nHighlights: ${highlights.trim()}`;
          }
          generatePayload.textContent = textContent;
        } else {
          throw new Error('Please upload a box score or describe what happened in the game.');
        }
      }

      // Step 2: Create the podcast job
      const generateResponse = await api.post('/api/podcasts/generate', generatePayload);
      const jobId = generateResponse.data.jobId;
      const createdPodcast = generateResponse.data.podcast || null;

      if (!jobId) {
        throw new Error('No job ID returned from podcast generation.');
      }

      // Step 3: Poll for completion
      const result = await podcastApi.pollForCompletion(jobId, (progress) => {
        if (onProgress) {
          try { onProgress(progress); } catch (_) { /* no-op */ }
        }
        console.log('Podcast generation progress:', progress);
      });

      // Attach the created podcast ID so the UI can deep-link
      const enrichedResult = createdPodcast?._id ? { ...result, _id: createdPodcast._id } : result;
      return { data: enrichedResult };
    } catch (error) {
      throw error;
    }
  },
};

// Team API
export const teamApi = {
  getTeams: () => api.get('/api/teams'),
  getTeam: (id) => api.get(`/api/teams/${id}`),
  createTeam: (teamData) => api.post('/api/teams', teamData),
  updateTeam: (id, teamData) => api.put(`/api/teams/${id}`, teamData),
  deleteTeam: (id) => api.delete(`/api/teams/${id}`),
};

// Auth API
export const authApi = {
  getMe: () => api.get('/auth/me'),
};

// Health check
export const healthCheck = async () => {
  try {
    const response = await api.get('/api/health');
    return response.data;
  } catch (error) {
    throw new Error('Backend server is not responding');
  }
};

export default api;
